import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useTheme } from "@mui/material";
import _ from 'lodash';
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { base64ToArrayBuffer } from "../../components/common/DocumentUpload";
import RightArrow from "../../components/icons/RightArrow";
import { bloqcibeApi, useAddFieldSubjectEnrollmentMutation, useGetSubjectDetailQuery, useGetTrialSiteInfoQuery, useRemoveFieldSubjectEnrollmentMutation, useSaveVisitAnswersMutation, useUploadCRFFileMutation, useUploadVisitSignatureMutation } from "../../store/slices/apiSlice";
import { openBackdropLoading } from "../../store/slices/applicationSlice";
import { openMessage } from "../../store/slices/showMessageSlice";
import { mergeCustomFields } from "../../util/util";
import DynamicFormEngine from "../dynamicFormEngine";
import { base64ImageToBlob, createNewFields, getUserRole } from "../util";
import DynamicFormAction from "../dynamicFormEngine/FormEntity/DynamicFormAction";
import originalConfig from './vir_crf.json';
import { setQueries, setQueriesLoading, clearQueries, selectHasOpenQueries, selectQueriesByFieldKey, selectRefetchTrigger } from '../../store/slices/querySlice';
import SubjectParticipantIdHeading from '../../components/common/SubjectParticipantIdHeading';

const local = false;

const SubjectCRFForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const crfDocumentKey = useSelector((state) => state?.trial?.trialSiteDetail?.siteTrialData?.trial?.crfForm);
  let { trialId, trialSiteId, siteInitStep, crfMasterId, subjectMasterId } = useParams();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const [originalFormConfig, setOriginalFormConfig] = useState(null);
  // const [formConfig, setFormConfig] = useState(null);
  const [formConfig, setFormConfig] = useState(local ? originalConfig.steps.find((_obj) => _obj.key == siteInitStep) : null);
  const [formAnswers, setFormAnswers] = useState();
  const {user: loggedInUser, userData} = useSelector((state) => state.auth);
  const [selectedSectionKey, setSelectedSectionKey] = useState();
  const [selectedSubSectionKey, setSelectedSubSectionKey] = useState();
  const [stepStatusData, setStepStatusData] = useState(null);
  const [subjectDetailWithSteps, setSubjectDetailWithSteps] = useState(null);
  const [dynamicFormAction, setDynamicFormAction] = useState(DynamicFormAction.NA);
  const [getSubjectDetail] = bloqcibeApi.endpoints.getSubjectDetail.useLazyQuery();
  const [getLibraryForm] = bloqcibeApi.endpoints.getLibraryForm.useLazyQuery();
  const [getEConsentFormConfig, { data: formData }] =
    bloqcibeApi.endpoints.getEConsentFormConfig.useLazyQuery();
  const [downloadVisitSignature] =
    bloqcibeApi.endpoints.downloadVisitSignature.useLazyQuery();
  const [downloadCRFFile] =
    bloqcibeApi.endpoints.downloadCRFFile.useLazyQuery();
  const [getVisitAnswers] = bloqcibeApi.endpoints.getVisitAnswers.useLazyQuery();
  const [getSubjectAnswers] =
  bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();
  const [getQueries] = bloqcibeApi.endpoints.getQueries.useLazyQuery();
  
  // Query selectors from Redux
  const hasOpenQueries = useSelector(selectHasOpenQueries);
  const queriesByFieldKey = useSelector(selectQueriesByFieldKey);
  const refetchTrigger = useSelector(selectRefetchTrigger);
  const [saveVisitAnswers] = useSaveVisitAnswersMutation();
  const [uploadVisitSignature] = useUploadVisitSignatureMutation();
  const [uploadCRFFile] = useUploadCRFFileMutation();
  const { data: trialSiteData } = useGetTrialSiteInfoQuery({
    sponsorId: sponsorId,
    trialId: trialId,
    siteTrialId: trialSiteId,
  });
  const [siteId, setSiteId] = useState(null);
  const [labelModel, setLabelModel] = useState({
    open: false,
    fieldKey: "",
  });
  const [label, setLabel] = useState("");
  const [addFieldSubjectEnrollment] = useAddFieldSubjectEnrollmentMutation();
  const [customFieldConfig, setCustomFieldConfig] = useState(null);
  const [removeFieldSubjectEnrollment] = useRemoveFieldSubjectEnrollmentMutation();
  useEffect(() => {
    (async () => {
      if (siteInitStep && trialSiteData && crfDocumentKey) {
        setSiteId(trialSiteData.siteTrialData.siteId);
        const _formDetailsData = await getLibraryForm(
          `${process.env.REACT_APP_API_ENDPOINT_URL}form-library/library/bloqcube/${crfDocumentKey}`
        );
        if (_formDetailsData.data) {
          // console.log('_formDetailsData.data', _formDetailsData.data);
          setOriginalFormConfig(_formDetailsData.data);
        } else if (_formDetailsData.error) {
          console.error('UNABLE TO GET FORM DETAILS');
        }

        const formDetailsData = await getEConsentFormConfig({
          sponsorId,
          trialId,
          siteId: trialSiteData.siteTrialData.siteId,
          documentKey: crfDocumentKey,
          payload: {
            subjectMasterId: subjectMasterId,
          },
        });
        if (formDetailsData.data) {
          setCustomFieldConfig(formDetailsData.data);
          // let _tempConfig = { ..._formDetailsData.data };
          let _tempConfig = local ? { ...originalConfig } : { ..._formDetailsData.data };
          const mergedConfig = mergeCustomFields(_tempConfig, formDetailsData.data);
          setFormConfig(mergedConfig.steps.find((_obj) => _obj.key === siteInitStep));
        } else if (formDetailsData.error) {
          console.error('UNABLE TO GET FORM DETAILS');
        } else {
          setFormConfig(_formDetailsData.data.steps.find((_obj) => _obj.key === siteInitStep));
        }
        await getSubjectAnswers({
          sponsorId,
          trialId,
          siteId: trialSiteData.siteTrialData.siteId,
          payload: {
            subjectMasterId: subjectMasterId,
          },
        });
      }
    })();
  }, [siteInitStep, trialSiteData, crfDocumentKey]);

  const userSiteTrialRole = useMemo(() => {
    return getUserRole(loggedInUser, trialId, trialSiteData?.siteTrialData?.site?.id, userData);
  }, [loggedInUser, trialSiteData, userData]);

  useEffect(() => {
    (async () => {
      const result = await getSubjectDetail(subjectMasterId);
      if (result.data) {
        setSubjectDetailWithSteps(result.data);
        const _stepStatusData = result.data?.crfDetail?.stepStatus?.find((_obj) => _obj.stepKey === siteInitStep);
        if (_stepStatusData) {
          setStepStatusData(_stepStatusData);
        }
      }
    })();
  }, [subjectMasterId, siteInitStep]);

  // editAllowed - Comprehensive permission check matching SubjectDetailCRFInfo.jsx logic
  const editAllowed = useMemo(() => {
    // Subject is On_Hold - no edit allowed
    if (subjectDetailWithSteps && subjectDetailWithSteps?.status === 'On_Hold') {
      return false;
    }

    // Block editing for terminal statuses
    if (
      stepStatusData &&
      ['Withdrawal', 'Enrollment_Failure', 'Screening_Failure', 'On_Hold'].includes(stepStatusData.status)
    ) {
      return false;
    }

    if (userSiteTrialRole && stepStatusData && stepStatusData.userAccess) {
      const userAccess = JSON.parse(stepStatusData.userAccess);
      const editAccess = userAccess.edit;
      const isAccess =
        typeof editAccess !== 'string'
          ? _.find(editAccess, (access) => {
              return access === userSiteTrialRole;
            })
          : editAccess === userSiteTrialRole;

      // Check readOnlyEdit configuration from step config
      const readOnlyEditConfig = formConfig?.readOnlyEdit;

      // Allow edit if user has edit access and no readOnlyEdit config
      if (isAccess && !readOnlyEditConfig) {
        return true;
      }

      if (readOnlyEditConfig) {
        const currentStatus = stepStatusData.status;

        // Check granular status-based role access
        if (readOnlyEditConfig.statusAccess && readOnlyEditConfig.statusAccess[currentStatus]) {
          const statusRoles = readOnlyEditConfig.statusAccess[currentStatus];
          const hasStatusAccess = Array.isArray(statusRoles)
            ? statusRoles.includes(userSiteTrialRole)
            : statusRoles === userSiteTrialRole;

          if (hasStatusAccess) {
            return true;
          }
        }

        // Check legacy format: allowApplicableStatus + roles
        const allowApplicableStatus = readOnlyEditConfig.allowApplicableStatus || [];
        const readOnlyEditRoles = readOnlyEditConfig.roles || readOnlyEditConfig.access?.edit || [];

        if (allowApplicableStatus.length > 0) {
          const isStatusAllowed = allowApplicableStatus.includes(currentStatus);
          const hasReadOnlyEditAccess = Array.isArray(readOnlyEditRoles)
            ? readOnlyEditRoles.includes(userSiteTrialRole)
            : readOnlyEditRoles === userSiteTrialRole;

          if (isStatusAllowed && hasReadOnlyEditAccess) {
            return true;
          }
        }
      }

      // Allow edit if there are open queries on the form (for resolving queries)
      if (hasOpenQueries) {
        const currentStatus = stepStatusData.status;
        const verifyAccess = userAccess.verify;
        const hasVerifyAccess = _.find(verifyAccess, (access) => access === userSiteTrialRole);

        // Allow editing for users with verify access when there are open queries
        if (hasVerifyAccess) {
          return true;
        }

        // Check readOnlyEdit for open queries
        const readOnlyEditConfig = formConfig?.readOnlyEdit;
        if (readOnlyEditConfig) {
          // Specific query resolution roles
          if (readOnlyEditConfig.queryResolutionAccess) {
            const queryRoles = readOnlyEditConfig.queryResolutionAccess;
            const hasQueryAccess = Array.isArray(queryRoles)
              ? queryRoles.includes(userSiteTrialRole)
              : queryRoles === userSiteTrialRole;

            if (hasQueryAccess) {
              return true;
            }
          }

          // Check if user has access for current status via statusAccess
          if (readOnlyEditConfig.statusAccess && readOnlyEditConfig.statusAccess[currentStatus]) {
            const statusRoles = readOnlyEditConfig.statusAccess[currentStatus];
            const hasStatusAccess = Array.isArray(statusRoles)
              ? statusRoles.includes(userSiteTrialRole)
              : statusRoles === userSiteTrialRole;

            if (hasStatusAccess) {
              return true;
            }
          }

          // Fallback to legacy roles
          const readOnlyEditRoles = readOnlyEditConfig.roles || readOnlyEditConfig.access?.edit || [];
          const hasReadOnlyEditAccess = Array.isArray(readOnlyEditRoles)
            ? readOnlyEditRoles.includes(userSiteTrialRole)
            : readOnlyEditRoles === userSiteTrialRole;

          if (hasReadOnlyEditAccess) {
            return true;
          }
        }
      }

      return false;
    }
    return false;
  }, [userSiteTrialRole, stepStatusData, subjectDetailWithSteps, hasOpenQueries, formConfig]);

  const onFormChanged = async (sectionKey, subSectionKey) => {
    setSelectedSectionKey(sectionKey)
    setSelectedSubSectionKey(subSectionKey)
    const _answers = await getVisitAnswers({
      sponsorId: sponsorId,
      trialId: trialId,
      siteId: trialSiteData?.siteTrialData?.site?.id,
      payload: {
        crfMasterId: crfMasterId,
        subjectMasterId: subjectMasterId,
        stepKey: siteInitStep,
        sectionKey: sectionKey,
        subSectionKey: subSectionKey,
      },
    });
    const result = await getSubjectDetail(subjectMasterId);
    if (result.data) {
      setSubjectDetailWithSteps(result.data);
      const _stepStatusData = result.data?.crfDetail?.stepStatus?.find((_obj) => _obj.stepKey === siteInitStep);
      if (_stepStatusData) {
        setStepStatusData(_stepStatusData);
      }
    }
    setFormAnswers(_answers.data);
  }

  // Initialize selectedSectionKey when formConfig loads (for forms without sections or on initial load)
  useEffect(() => {
    if (formConfig && !selectedSectionKey) {
      // If form has sections, use first section key
      if (formConfig.sections && formConfig.sections.length > 0) {
        setSelectedSectionKey(formConfig.sections[0].key);
        if (formConfig.sections[0].subTabs && formConfig.sections[0].subTabs.length > 0) {
          setSelectedSubSectionKey(formConfig.sections[0].subTabs[0].key);
        }
      } else {
        // For forms without sections, use the step/form key itself
        setSelectedSectionKey(formConfig.key || siteInitStep);
      }
    }
  }, [formConfig, selectedSectionKey, siteInitStep]);

  // Fetch queries for the selected form section
  useEffect(() => {
    const fetchQueries = async () => {
      const currentSiteId = trialSiteData?.siteTrialData?.site?.id;
      if (!sponsorId || !trialId || !currentSiteId || !subjectMasterId || !siteInitStep) {
        return;
      }
      
      dispatch(setQueriesLoading(true));
      try {
        const result = await getQueries({
          sponsorId,
          trialId,
          siteId: currentSiteId,
          stepKey: siteInitStep,
          sectionKey: selectedSectionKey || '',
          subSectionKey: selectedSubSectionKey || '',
          subjectMasterId,
          page: 1,
          limit: 100,
          active: true,
        });

        if (result.data) {
          const fetchedQueries = result.data.data || [];
          dispatch(setQueries(fetchedQueries));
        } else {
          dispatch(setQueries([]));
        }
      } catch (error) {
        console.error('Error fetching queries:', error);
        dispatch(setQueries([]));
      }
    };

    fetchQueries();
  }, [sponsorId, trialId, trialSiteData?.siteTrialData?.site?.id, subjectMasterId, siteInitStep, selectedSectionKey, selectedSubSectionKey, dispatch, getQueries, refetchTrigger]);

  // Clear queries when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearQueries());
    };
  }, [dispatch]);
  const handleUploadFile = async (keys, fieldName, setFieldValueFun) => {
    const formData = new FormData();
    formData.append("file", keys.file);
    formData.append("crfMasterId", crfMasterId);
    formData.append("stepKey", keys.stepKey);
    formData.append("sectionKey", keys.sectionKey);
    formData.append("subSectionKey", keys.subSectionKey ? keys.subSectionKey : '');
    formData.append("categoryKey", keys.categoryKey);
    formData.append("fieldKey", keys.fieldKey);
    const result = await uploadCRFFile({
      payload: formData,
      sponsorId: sponsorId,
      trialId: trialId,
      siteId: trialSiteData?.siteTrialData?.site?.id,
    });
    setFieldValueFun(fieldName, result.data.s3Key);
  }
  const handleSaveSignature = async (keys, fieldName, setFieldValueFun) => {
    const formData = new FormData();
    formData.append("file", base64ImageToBlob(keys.file));
    formData.append("crfMasterId", crfMasterId);
    formData.append("stepKey", keys.stepKey);
    formData.append("sectionKey", keys.sectionKey);
    formData.append("subSectionKey", keys.subSectionKey ? keys.subSectionKey : '');
    formData.append("categoryKey", keys.categoryKey);
    formData.append("fieldKey", keys.fieldKey);
    const result = await uploadVisitSignature({
      payload: formData,
      sponsorId: sponsorId,
      trialId: trialId,
      siteId: trialSiteData?.siteTrialData?.site?.id,
    });
    setFieldValueFun(fieldName, result?.data?.s3Key);
  }
  const handleDownloadSignature = async (s3Key, setSign) => {
    const result = await downloadVisitSignature({
      s3Key: s3Key,
    });
    if (result.data) {
      const byteArray = base64ToArrayBuffer(result.data);
      const rr = new Blob([byteArray], { type: "application/octet-stream" });
      const reader = new FileReader();
      reader.readAsDataURL(rr);
      reader.onloadend = function () {
        const base64data = reader.result;
        setSign(base64data);
      };
      reader.onerror = function () {
        setSign(null);
      };
    } else {
      console.log("Failed to load signature");
      setSign(null);
    }
  }
  const handleDownloadFile = async (s3Key, fileName, setSelectedFile) => {
    const result = await downloadCRFFile({
      s3Key: s3Key,
    });
    if (result.data) {
      let byteArray = base64ToArrayBuffer(result.data);
      let a = window.document.createElement("a");
      a.href = window.URL.createObjectURL(
        new Blob([byteArray], { type: "application/octet-stream" })
      );
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setSelectedFile && setSelectedFile(new Blob([byteArray], { type: "application/octet-stream" }))
    } else {
      console.log("Failed to load file");
    }
  }
  const handleSave = async (data) => {
    try {
      const payload = {
        crfMasterId: crfMasterId,
        subjectMasterId: subjectMasterId,
        documentKey: originalFormConfig?.documentKey,
        documentVersion: originalFormConfig?.documentVersion,
        stepKey: data.stepKey,
        sectionKey: data.sectionKey,
        subSectionKey: data.subSectionKey ? data.subSectionKey : '',
        answers: data.answers,
      };

      dispatch(openBackdropLoading({ backdropLoading: true }));

      const response = await saveVisitAnswers({
        sponsorId,
        trialId,
        siteId: trialSiteData?.siteTrialData?.site?.id,
        payload,
      });

      if (response.data) {
        if (response.data?.withdrawal) {
          navigate(-2);
          dispatch(bloqcibeApi.util.invalidateTags(['SubjectList', 'SubjectWithdrawList',]));
          dispatch(openBackdropLoading({ backdropLoading: false }));
          dispatch(openMessage({ messageSeverity: 'warning', message: response.data?.message }));
        } else if (response.data.status === 'Enrollment_Failure') {
          navigate(-2);
          dispatch(bloqcibeApi.util.invalidateTags(['SubjectList', 'SubjectWithdrawList',]));
          dispatch(openBackdropLoading({ backdropLoading: false }));
          dispatch(openMessage({ messageSeverity: 'warning', message: 'Subject has failed screening process.' }));
        } else {
          // Pass API response message if present, otherwise use default
          const apiMessage = response.data?.message;
          setDynamicFormAction(apiMessage 
            ? { type: DynamicFormAction.FORM_SAVE_SUCCESS, message: apiMessage }
            : DynamicFormAction.FORM_SAVE_SUCCESS
          );
        }
      } else if (response.error) {
        // Pass API error message if present
        const errorMessage = response.error?.data?.message;
        setDynamicFormAction(errorMessage
          ? { type: DynamicFormAction.FORM_SAVE_FAILURE, message: errorMessage }
          : DynamicFormAction.FORM_SAVE_FAILURE
        );
      }
    } catch (error) {
      setDynamicFormAction(DynamicFormAction.FORM_SAVE_FAILURE);
      return Promise.reject(error);
    }
  }

  const findObjectByValue = async (obj, targetValue, path = [], keys) => {
    for (const key in obj) {
      if (obj[key] === targetValue) {
        let configFields = obj?.addFields;
        let needLabelField = _.find(configFields, {
          needLabel: true,
        });
        if (configFields && needLabelField && !label) {
          setLabelModel({
            open: true,
            fieldKey: obj.key,
          });
          return null;
        } else if (configFields && needLabelField && label) {
          const attachedLabel = configFields.map((field) => {
            if (field.needLabel === true) {
              return {
                ...field,
                label: label,
              };
            } else {
              return field;
            }
          });
          const newFields = createNewFields(
            attachedLabel,
            obj.fields.length + 1
          );
          const _formConfig = await addFieldSubjectEnrollment({
            sponsorId,
            trialId,
            siteId: siteId,
            payload: {
              documentKey: keys.documentKey,
              stepKey: siteInitStep,
              subjectMasterId: subjectMasterId,
              sectionKey: keys.sectionKey,
              subSectionKey: keys.subSectionKey,
              categoryKey: keys.categoryKey,
              fields: newFields,
            },
          });
          setCustomFieldConfig(_formConfig.data);
          // let _tempConfig = { ...originalFormConfig };
          let _tempConfig = local ? { ...originalConfig } : { ...originalFormConfig };
          const mergedConfig = mergeCustomFields(_tempConfig, _formConfig.data);
          setFormConfig(
            mergedConfig.steps.find((_obj) => _obj.key === siteInitStep)
          );
          break;
        } else if (configFields) {
          const newFields = createNewFields(
            configFields,
            obj.fields.length + 1
          );
          const _formConfig = await addFieldSubjectEnrollment({
            sponsorId,
            trialId,
            siteId: siteId,
            payload: {
              documentKey: keys.documentKey,
              stepKey: siteInitStep,
              subjectMasterId: subjectMasterId,
              sectionKey: keys.sectionKey,
              subSectionKey: keys.subSectionKey,
              categoryKey: keys.categoryKey,
              fields: newFields,
            },
          });
          setCustomFieldConfig(_formConfig.data);
          // let _tempConfig = { ...originalFormConfig };
          let _tempConfig = local ? { ...originalConfig } : { ...originalFormConfig };
          const mergedConfig = mergeCustomFields(_tempConfig, _formConfig.data);
          setFormConfig(
            mergedConfig.steps.find((_obj) => _obj.key === siteInitStep)
          );
          break;
        } else {
          console.error("'newFields' key not found ");
        }
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        // Continue searching in the nested object
        const result = await findObjectByValue(
          obj[key],
          targetValue,
          [...path, key],
          keys
        );
        if (result) {
          return result;
        }
      }
    }
    // Value not found in the current object
    return null;
  };

  const addNewField = async (parentKey, keys) => {
    const valChange = JSON.parse(JSON.stringify(formConfig));
    await findObjectByValue(valChange, parentKey, [], {
      documentKey: crfDocumentKey,
      ...keys,
    });
  };

  const removeCustomField = async (field, keys) => {
    if (customFieldConfig) {
      dispatch(openBackdropLoading({ backdropLoading: true }));
      const _customFieldConfig = _.filter(
        customFieldConfig,
        (config) =>
          config?.stepKey === siteInitStep &&
          config?.sectionKey === keys?.sectionKey &&
          config?.subSectionKey === keys?.subSectionKey &&
          config?.categoryKey === keys?.categoryKey
      );
      const deleteFieldData = _.find(_customFieldConfig, (fieldConfig) =>
        _.some(fieldConfig?.customFields, (_field) => _field.key === field.key)
      );
      const updatedConfig = await removeFieldSubjectEnrollment(deleteFieldData?.id);
      if (updatedConfig.data) {
        setCustomFieldConfig(updatedConfig.data);
        let _tempConfig = local ? { ...originalConfig } : { ...originalFormConfig };
        // let _tempConfig = { ...originalFormConfig };
        const mergedConfig = mergeCustomFields(_tempConfig, updatedConfig.data);
        setFormConfig(mergedConfig.steps.find((_obj) => _obj.key === siteInitStep));
      } else {
        dispatch(openMessage({ messageSeverity: 'error', message: 'Failed to remove field!' }));
      }
      dispatch(openBackdropLoading({ backdropLoading: false }));
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
        <Box onClick={() => navigate(-1)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}>
          <RightArrow leftArrow />
          <Typography variant="subtitle1" sx={{ textTransform: 'none', color: theme.palette.primary.main }}>
            Back
          </Typography>
        </Box>
        <SubjectParticipantIdHeading />
        <Box sx={{ minWidth: 0 }} />
      </Box>
      {formConfig && trialSiteData && (
        <DynamicFormEngine
          formConfig={formConfig}
          userSiteTrialRole={userSiteTrialRole}
          readOnly={!editAllowed}
          onFormChanged={onFormChanged}
          handleSave={(payload) => {
            handleSave(payload);
          }}
          handleSaveSignature={handleSaveSignature}
          handleUploadFile={handleUploadFile}
          handleDownloadFile={handleDownloadFile}
          handleDownloadSignature={handleDownloadSignature}
          formAnswers={formAnswers}
          AddNewField={addNewField}
          stepStatusData={stepStatusData}
          removeCustomField={removeCustomField}
          dynamicFormAction={dynamicFormAction}
          setDynamicFormAction={setDynamicFormAction}
          queriesByFieldKey={queriesByFieldKey}
          hasOpenQueries={hasOpenQueries}
        />
      )}
      <Dialog
        onClose={() => {
          setLabelModel({
            ...labelModel,
            open: false,
          });
        }}
        maxWidth={'sm'}
        open={labelModel.open}
        sx={{
          dialogPaper: {
            minHeight: '80vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>Enter label</DialogTitle>
        <DialogContent sx={{ minHeight: '10vh', minWidth: '40vW' }}>
          <TextField label="Label" value={label} sx={{ mt: 2 }} onChange={(e) => setLabel(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelModel({ ...labelModel, open: false })}>Cancel</Button>
          <Button
            onClick={() => {
              addNewField(labelModel.fieldKey);
              setLabelModel({ ...labelModel, open: false, fieldKey: '' });
              setLabel('');
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubjectCRFForm;